import { Action, Actions, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { ChangeBrickScaling, ChangeBrickTitle, SaveAllChangedBrickSettings } from './common-brick-settings.actions';
import { BrickInstancesSelectors } from '../../brick-instances/brick-instances.selectors';
import { BrickSettingsService } from '../../../../../../generated/swagger';
import { BrickSettingsChangedByServer } from '../brick-settings.actions';
import { CommonBrickSettingRemoteDto } from '../../../../../../generated/signalR/common-brick-setting-remote-dto';
import { getBrickSettingModel } from './getBrickSettingModelFunction';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { StartEditingBrickSettings, StopEditingBrickSettings } from '../../brick-instances/brick-instances.actions';

export interface CommonBrickSettingsStateModel {
   brickSettings: { [brickId: string]: CommonBrickSettingRemoteDto };
   brickSettingsEditMode: { [brickId: string]: CommonBrickSettingRemoteDto };
}

@State<CommonBrickSettingsStateModel>({
   name: 'commonBrickSettings',
   defaults: {
      brickSettings: {},
      brickSettingsEditMode: null,
   },
})
@Injectable()
export class CommonBrickSettingsState {
   constructor(private brickSettingsService: BrickSettingsService, private store: Store, private actions$: Actions) {}

   @Action(ChangeBrickTitle)
   @ImmutableContext()
   public changeBrickTitle(ctx: StateContext<CommonBrickSettingsStateModel>, action: ChangeBrickTitle) {
      ctx.setState((state) => {
         const brickSettingModel = getBrickSettingModel(state, action.brickInstanceId);
         brickSettingModel.brickOverwrittenTitle = action.newTitle;
         state.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeBrickScaling)
   @ImmutableContext()
   public changeBrickScaling(ctx: StateContext<CommonBrickSettingsStateModel>, action: ChangeBrickScaling) {
      ctx.setState((state) => {
         const brickSettingModel = getBrickSettingModel(state, action.brickInstanceId);
         brickSettingModel.brickScaling = action.newScaling;
         state.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(StartEditingBrickSettings)
   public startEditingBrickSettings(ctx: StateContext<CommonBrickSettingsStateModel>, action: StartEditingBrickSettings) {
      if (!_.isNil(ctx.getState().brickSettingsEditMode)) {
         return;
      }
      ctx.patchState({
         brickSettingsEditMode: _.cloneDeep(ctx.getState().brickSettings),
      });
   }

   @Action(StopEditingBrickSettings)
   public stopEditingBrickSettings(ctx: StateContext<CommonBrickSettingsStateModel>, action: StopEditingBrickSettings) {
      ctx.patchState({
         brickSettingsEditMode: null,
      });
   }

   @Action(BrickSettingsChangedByServer)
   @ImmutableContext()
   public brickSettingsChangedByServer(ctx: StateContext<CommonBrickSettingsStateModel>, action: BrickSettingsChangedByServer) {
      ctx.setState((state) => {
         _.forEach(action.brickSettings, (value, key) => {
            state.brickSettings[key] = _.cloneDeep(value.commonBrickSettingRemoteDto);
         });
         return state;
      });
   }

   @Action(SaveAllChangedBrickSettings)
   public saveAllChangedBrickSettings(ctx: StateContext<CommonBrickSettingsStateModel>, action: SaveAllChangedBrickSettings) {
      const observables = action.brickIds.map((brickInstanceId) => this.saveBrickSettings(ctx.getState(), brickInstanceId)).filter((x) => !_.isNil(x));
      if (_.isEmpty(observables)) {
         return;
      }

      return forkJoin(observables);
   }

   private saveBrickSettings(state: CommonBrickSettingsStateModel, brickInstanceId: string): Observable<any> {
      const brickDescription = this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(brickInstanceId);

      if (!_.isNil(brickDescription.definition.settingStateHandler) && !(this instanceof brickDescription.definition.settingStateHandler)) {
         // another specific brick setting state should be responsible for this action!
         return;
      }

      const brickSetting = getBrickSettingModel(state, brickInstanceId, false);
      const brickSettingUnchanged = getBrickSettingModel(state, brickInstanceId, false, true);

      if (_.isNil(brickSetting) || _.isEqual(brickSetting, brickSettingUnchanged)) {
         return;
      }

      return this.brickSettingsService.changeGeneralBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: {
            commonBrickSettingRemoteDto: brickSetting,
         },
      });
   }
}
